import { trpc, toastTrpcError } from "@/lib/trpc";
import { toast } from "sonner";

interface UseRunDialogMutationsParams {
  scenarioId: string;
  projectId: string;
  onAutoRunSuccess: (walkthroughId: string) => void;
  onDesignReviewCreated: (walkthroughId: string) => void;
  onDesignReviewExecuted: () => void;
}

export function useRunDialogMutations({
  scenarioId,
  projectId,
  onAutoRunSuccess,
  onDesignReviewCreated,
  onDesignReviewExecuted,
}: UseRunDialogMutationsParams) {
  const utils = trpc.useUtils();

  const autoRunMutation = trpc.walkthrough.autoRun.useMutation({
    onSuccess: (data) => {
      toast.success(
        "Walkthrough started — steps will appear as the persona navigates",
      );
      utils.walkthrough.list.invalidate({ scenarioId });
      onAutoRunSuccess(data.walkthroughId);
    },
    onError: (err) => toastTrpcError(err, "Failed to start walkthrough"),
  });

  const createDesignReviewMutation =
    trpc.walkthrough.createDesignReview.useMutation({
      onSuccess: (data) => {
        onDesignReviewCreated(data.id);
      },
      onError: (err) => toastTrpcError(err, "Failed to create design review"),
    });

  const executeDesignReviewMutation =
    trpc.walkthrough.executeDesignReview.useMutation({
      onSuccess: () => {
        toast.success("Design review completed");
        utils.walkthrough.list.invalidate({ scenarioId });
        utils.finding.list.invalidate({ projectId });
        utils.finding.queueCounts.invalidate({ projectId });
        onDesignReviewExecuted();
      },
      onError: (err) => toastTrpcError(err, "Failed to execute design review"),
    });

  return {
    autoRunMutation,
    createDesignReviewMutation,
    executeDesignReviewMutation,
    isRunning:
      autoRunMutation.isPending ||
      createDesignReviewMutation.isPending ||
      executeDesignReviewMutation.isPending,
  };
}
